import { useContext } from "react";
import { BaseWidget } from "./BaseWidget";
import PlusOneOutlinedIcon from '@mui/icons-material/PlusOneOutlined';
import { ColorControl } from "../controls/ColorControl";
import { SelectControl } from "../controls/SelectControl";
import { NumberCssControl } from "../controls/NumberCssControl";
import { IconButtonGroupControl } from "../controls/IconButtonGroupControl";
import { PeriodOverrideControl } from "../controls/PeriodOverrideControl";
import useWidgetPeriod from "./_shared/useWidgetPeriod";
import useWidgetPersona from "./_shared/useWidgetPersona";
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import NumbersIcon from '@mui/icons-material/Numbers';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { db } from "../../../../firebase/config";
import { client } from "../../../../hooks/Client";
import { useFirestoreGeneral } from "../../../../firebase/useFirestore";
import { TemplateDataContext } from "../context/TemplateDataContext";
import { fetchTemplateData } from "../utils/fetchTemplateData";
import buildOutputChoices from "../utils/buildOutputChoices";
import OutputVisualization from "../../../dataVisualizations/OutputVisualization";
import WidgetSourcePlaceholder from "../components/WidgetSourcePlaceholder";
import LoadingSpinner from "../../../common/LoadingSpinner";

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// OutputWidget — single-Output chart. Renders via the shared
// `OutputVisualization` so it matches the dashboard Output card.

/**
 * Functional wrapper so we can use useContext inside the widget render.
 * @param {object} props
 * @param {string} props.outputId
 * @param {'line' | 'bar' | 'number'} props.graphType
 * @param {string} props.seriesColor - Colour of the data itself (bar, line or number).
 * @param {string} props.labelLegendColor - Colour of the axis labels and legend.
 * @param {string} [props.numberFontSize] - CSS font-size (e.g. '48px') for the 'number' type.
 * @param {'left' | 'center' | 'right'} [props.numberTextAlign] - Alignment for the 'number' type.
 * @param {string} [props.numberFontWeight] - Font weight for the 'number' type.
 * @param {{mode?: string, start?: string|null, end?: string|null}} [props.period] - Widget period setting (PeriodOverrideControl)
 * @returns {JSX.Element}
 */
const OutputWidgetRenderer = ({ outputId, graphType, seriesColor, labelLegendColor, numberFontSize, numberTextAlign, numberFontWeight, period }) => {

    const { templateData, isLoading, error, isTemplate } = useContext(TemplateDataContext);
    const personaId = useWidgetPersona();
    const { range: periodRange, startDate, endDate } = useWidgetPeriod(period);

    /* Existence check, separate from the chart's own data fetch: the hook
       returns '' while resolving and an array once Firestore answered. An
       empty array means the saved outputId doesn't exist in this tenant —
       the orphan state every data widget lands in after a template copy. */
    const liveOutputs = useFirestoreGeneral('Outputs', 'ID', isTemplate ? '' : (outputId || ''));
    const outputMissing = !outputId
        || (!isTemplate && Array.isArray(liveOutputs) && liveOutputs.length === 0);

    return (
        <div className="output-widget-wrapper">
            {isLoading && <LoadingSpinner />}
            {error && <p className="output-widget-error">Fout bij laden template data.</p>}

            {!isLoading && !error && outputMissing && (
                <WidgetSourcePlaceholder ctaLabel="Selecteer output" />
            )}

            {!isLoading && !error && !outputMissing && (
                <OutputVisualization
                    outputId={outputId}
                    graphType={graphType}
                    /* OutputVisualization resolves the single-output data colour
                       from colorPalette[0]; graphColor drives the axes + legend. */
                    colorPalette={[seriesColor]}
                    graphColor={labelLegendColor}
                    numberFontSize={numberFontSize}
                    numberTextAlign={numberTextAlign}
                    numberFontWeight={numberFontWeight}
                    height="100%"
                    selectedPersonaId={personaId}
                    startDate={startDate}
                    endDate={endDate}
                    periodRange={periodRange}
                    templateOutputData={isTemplate ? templateData?.data?.outputs : null}
                />
            )}
        </div>
    )
}

export class OutputWidget extends BaseWidget {

    getName() { return 'Output'; }
    getCategory() { return 'research'; }
    getIcon() { return <PlusOneOutlinedIcon />; }

    getControls() {
        return {
            'outputId': new SelectControl({
                label: 'Output',
                description: 'Output data om weer te geven',
                defaultValue: '',
                options: async () => {
                    const isTemplate = new URLSearchParams(window.location.search).get('template') === 'true'
                    let outputs = [];
                    let activities = [];

                    if (isTemplate) {
                        const json = await fetchTemplateData()
                        outputs = json?.data?.outputs?.outputs || []
                        activities = json?.data?.activities || []
                    } else {
                        // Geen orderBy: buildOutputChoices sorteert zelf, en een
                        // orderBy laat documenten zonder Position stil weg.
                        // AI-CONTEXT: See docs/pitfalls.md#database-firestore-orderby-excludes-missing-field
                        const [outputsSnap, activitiesSnap] = await Promise.all([
                            db.collection('Outputs').where('CompagnyID', '==', client || '').get(),
                            db.collection('Activities').where('CompagnyID', '==', client || '').get(),
                        ]);
                        outputs = outputsSnap.docs.map(doc => doc.data());
                        activities = activitiesSnap.docs.map(doc => doc.data());
                    }

                    // 'Activiteit · Output': dezelfde outputtitel komt bij
                    // meerdere activiteiten voor (updateverzoek Team Sportclub).
                    return {
                        choices: buildOutputChoices(outputs, activities),
                        allowSearch: true,
                    };
                }
            }),
            'period': new PeriodOverrideControl({
                label: 'Periode',
                description: 'Welke periode de data van deze widget toont.',
                defaultValue: { mode: 'inherit', start: null, end: null },
            }),
            'graphType': new IconButtonGroupControl({
                label: 'Grafiektype',
                defaultValue: 'line',
                options: {
                    buttons: [
                        { value: 'line', icon: <ShowChartIcon />, tooltip: 'Lijn' },
                        { value: 'bar', icon: <BarChartIcon />, tooltip: 'Staaf' },
                        { value: 'number', icon: <NumbersIcon />, tooltip: 'Totaal (alleen getal)' },
                    ]
                },
            }),
            'graphColor': new ColorControl({
                label: 'Grafiekkleur',
                description: 'Kleur van de balk, lijn of het getal',
                defaultValue: '#47acc3',
            }),
            'labelLegendColor': new ColorControl({
                label: 'Label- en legendakleur',
                description: 'Kleur van de labels en de legenda',
                defaultValue: '#555555',
            }),
            /* Number-only styling. `visibleWhen` hides these unless the
               'Totaal (alleen getal)' graph type is active. */
            'numberFontSize': new NumberCssControl({
                label: 'Tekstgrootte',
                description: 'Grootte van het getal',
                defaultValue: { value: 48, unit: 'px' },
                options: {
                    min: 8,
                    max: 200,
                    step: 1,
                    units: ['px'],
                },
                visibleWhen: (w) => w.getSetting('graphType') === 'number',
            }),
            'numberTextAlign': new IconButtonGroupControl({
                label: 'Uitlijning',
                description: 'Uitlijning van het getal',
                defaultValue: 'center',
                options: {
                    buttons: [
                        { value: 'left', icon: <FormatAlignLeftIcon />, tooltip: 'Links' },
                        { value: 'center', icon: <FormatAlignCenterIcon />, tooltip: 'Midden' },
                        { value: 'right', icon: <FormatAlignRightIcon />, tooltip: 'Rechts' },
                    ]
                },
                visibleWhen: (w) => w.getSetting('graphType') === 'number',
            }),
            'numberFontWeight': new SelectControl({
                label: 'Dikte',
                description: 'Dikte van het getal',
                defaultValue: 'bold',
                options: {
                    choices: [
                        { value: 'normal', label: 'Normaal' },
                        { value: 'bold', label: 'Vet' },
                        { value: 'bolder', label: 'Zwaarder' },
                        { value: 'lighter', label: 'Lichter' },
                    ],
                },
                visibleWhen: (w) => w.getSetting('graphType') === 'number',
            }),
            /* Note: 'height' is used in getStyles() — it sizes the canvas node
               so the chart (which fills 100%) can't overflow its container. */
            'height': new NumberCssControl({
                label: 'Hoogte',
                description: 'Hoogte van de grafiek',
                defaultValue: { value: 300, unit: 'px' },
                options: {
                    min: 50,
                    max: 2000,
                    step: 10,
                    units: ['px'],
                },
            }),
        };
    }

    /** @type {BaseWidget['getStyles']} */
    getStyles() {
        /* The number display sizes to its own content; only the chart needs a
           fixed box to fill, so we leave the node height alone for 'number'. */
        if (this.getSetting('graphType') === 'number') return {};
        return { height: this.getCssValue('height') };
    }

    render(props) {
        return <OutputWidgetRenderer
            outputId={props.outputId}
            graphType={/** @type {'line' | 'bar' | 'number'} */ (props.graphType)}
            seriesColor={props.graphColor}
            labelLegendColor={props.labelLegendColor}
            numberFontSize={props.numberFontSize ? this.getCssValue(props.numberFontSize) : undefined}
            numberTextAlign={props.numberTextAlign}
            numberFontWeight={props.numberFontWeight}
            period={props.period}
        />;
    }
}
